import stringify from 'fast-json-stable-stringify';
import pino from 'pino';
import { v4 as uuid } from 'uuid';
// --- Audit log ---------------------------------------------------------------
export const auditLogActionTypes = [
    'create',
    'update',
    'delete',
    'provision',
    'unprovision',
    'login',
    'sso-login',
    'forgot-password',
];
export function resolveLoggerInit(init) {
    return { ...init, instanceId: init.instanceId ?? uuid() };
}
/**
 * Materialises {@link ICortexLoggerInit} from Docker/service-style env names.
 * Use when you need a custom env object instead of **`resolveDefaultLoggerInit()`**.
 */
export function buildLoggerInitFromEnv(env) {
    const endpoint = env.LOG_API_ENDPOINT;
    const logLevelRaw = env.LOG_LEVEL;
    return {
        logLevel: logLevelRaw ?? 'debug',
        logJson: env.LOG_JSON !== 'false',
        logToConsole: env.LOG_TO_CONSOLE !== 'false',
        logApiEndpoint: endpoint,
        app: env.APP ?? '',
        namespace: env.NAMESPACE ?? '',
        buildTag: env.BUILD_TAG ?? '',
        auditLogsEnabled: env.AUDIT_LOGS_ENABLED === 'true',
    };
}
// --- Error shaping (stdout + log-api) ----------------------------------------
const stableStringify = stringify;
function toStringSafe(input) {
    return stableStringify(input, { cycles: true });
}
function isObject(value) {
    return !!value && (typeof value === 'object' || typeof value === 'function');
}
/** Inlined from legacy `errorConstants` naming. */
const tagRestricted = 'missing tag(s) to access this resource';
const allowKeys = new Set(['query', 'tagKey'].map((k) => k.toLowerCase()));
const whiteListedTerms = [
    tagRestricted,
    'expected one of:',
    'maybe you meant:',
    'in query:',
    'while processing query',
    'column',
];
const redactKeys = ['password', 'secret', 'token', 'key', 'buffer', 'authorization'];
const pii = new Set(['email', 'firstName', 'lastName', 'phoneNumber', 'phoneNumbers']);
const isRecord = (value) => isObject(value);
const isBlacklisted = (key) => redactKeys.some((k) => key.toLowerCase().includes(k.toLowerCase())) || pii.has(key);
function redact(object, visited = new WeakSet()) {
    if (visited.has(object))
        return '[ RECURSIVE ]';
    if (Array.isArray(object)) {
        visited.add(object);
        return object.map((val) => redact(val, visited));
    }
    if (isRecord(object)) {
        visited.add(object);
        return Object.keys(object).reduce((acc, key) => {
            if (allowKeys.has(key.toLowerCase())) {
                acc[key] = object[key];
                return acc;
            }
            acc[key] = isBlacklisted(key) ? '[ REDACTED ]' : redact(object[key], visited);
            return acc;
        }, {});
    }
    return typeof object === 'string' &&
        !whiteListedTerms.some((t) => object.toLowerCase().includes(t)) &&
        isBlacklisted(object)
        ? '[ REDACTED ]'
        : object;
}
function errorToObject(error, options = {}) {
    const { redacted = true } = options;
    const redactObject = (x) => (redacted ? redact(x) : x);
    if (!error.stack)
        return { message: toStringSafe(redactObject(error)) };
    const { name, message, stack, ...meta } = error;
    return {
        name,
        message: message !== undefined ? message : toStringSafe(redactObject(error)),
        stack,
        meta: meta && Object.keys(meta).length === 0 ? undefined : redactObject(meta),
    };
}
function errorToString(error, options = {}) {
    const { message, stack, meta } = errorToObject(error, options);
    return `${stack || message || ''}${meta && Object.keys(meta).length > 0 ? ` -- ${toStringSafe(meta)}` : ''}`;
}
function parsePinoNdjsonLine(line) {
    const trimmed = line.trimEnd();
    if (!trimmed)
        return null;
    try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
function pinoLevelNumberToLabel(n) {
    if (n >= 60)
        return 'fatal';
    if (n >= 50)
        return 'error';
    if (n >= 40)
        return 'warn';
    if (n >= 30)
        return 'info';
    if (n >= 20)
        return 'debug';
    return 'trace';
}
function createLineBufferDestination(onLine) {
    let buf = '';
    return {
        write(msg) {
            buf += typeof msg === 'string' ? msg : String(msg);
            let i;
            while ((i = buf.indexOf('\n')) !== -1) {
                const line = buf.slice(0, i);
                buf = buf.slice(i + 1);
                const t = line.trimEnd();
                if (t)
                    onLine(t);
            }
        },
    };
}
const pinoRecordReserved = new Set([
    'level',
    'time',
    'msg',
    'pid',
    'hostname',
    'v',
    'name',
    'error',
    'err',
]);
function formatStructuredStdoutLine(line, logJson, baseMeta) {
    const rec = parsePinoNdjsonLine(line);
    if (!rec)
        return '';
    const ts = typeof rec.time === 'number' ? rec.time : Date.now();
    const level = pinoLevelNumberToLabel(typeof rec.level === 'number' ? rec.level : 30);
    let message = String(rec.msg ?? '');
    if (message.length > 100_000) {
        message = `${message.slice(0, 98_999)} ... [truncated] ... ${message.slice(-1000)}`;
    }
    const rawErr = rec.error ?? rec.err;
    const errObj = rawErr !== undefined && rawErr !== null ? errorToObject(rawErr) : undefined;
    const meta = { ...baseMeta };
    for (const [k, v] of Object.entries(rec)) {
        if (!pinoRecordReserved.has(k))
            meta[k] = v;
    }
    if (logJson) {
        return stableStringify({ timestamp: ts, level, message, ...(errObj !== undefined ? { error: errObj } : {}), meta }, { cycles: true });
    }
    let out = `${new Date(ts).toISOString()} ${level}: ${message} `;
    if (Object.keys(meta).length > 0)
        out += stableStringify(meta, { cycles: true });
    if (errObj !== undefined)
        out += `\n${errorToString(errObj)}`;
    return out;
}
function logApiLogsUrl(endpoint) {
    return `${endpoint.replace(/\/+$/, '')}/logs`;
}
async function postLineToLogApi(endpoint, metadataRef, line) {
    const record = parsePinoNdjsonLine(line);
    if (!record)
        return;
    const timeMs = typeof record.time === 'number' ? record.time : Date.now();
    const levelNum = typeof record.level === 'number' ? record.level : 30;
    const rawErr = record.error ?? record.err;
    const errPayload = rawErr !== undefined && rawErr !== null
        ? errorToObject(rawErr)
        : undefined;
    const meta = {};
    for (const [key, val] of Object.entries(record)) {
        if (!pinoRecordReserved.has(key))
            meta[key] = val;
    }
    Object.assign(meta, metadataRef);
    const body = {
        logs: [
            {
                timestamp: new Date(timeMs).toISOString(),
                level: pinoLevelNumberToLabel(levelNum),
                message: String(record.msg ?? '').substring(0, 1000),
                error: errPayload,
                meta,
            },
        ],
    };
    try {
        const res = await fetch(logApiLogsUrl(endpoint), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            let text = '';
            try {
                text = await res.text();
            }
            catch {
                /* empty */
            }
            throw new Error(`log-api ${res.status}: ${text}`);
        }
    }
    catch (err) {
        console.error(`Failed to log to log-api: ${errorToString(err)}`);
    }
}
function createLogApiDestination(endpoint, metadataRef) {
    return createLineBufferDestination((singleLine) => {
        postLineToLogApi(endpoint, metadataRef, singleLine).catch((reason) => {
            console.error(`Failed to log to log-api: ${errorToString(reason)}`);
        });
    });
}
// --- Root filter -------------------------------------------------------------
const cortexRootLevels = [
    'debug',
    'info',
    'warn',
    'error',
];
function normalizeRootLogLevel(raw) {
    const lowered = raw.trim().toLowerCase();
    if (cortexRootLevels.includes(lowered))
        return lowered;
    return 'debug';
}
function isLikelyBackendNode() {
    return (typeof process !== 'undefined' && typeof process.cwd === 'function' && process.env !== undefined);
}
function tryImportMetaEnv() {
    try {
        const env = import.meta.env;
        if (env !== undefined && typeof env === 'object' && env !== null && 'MODE' in env)
            return env;
    }
    catch {
        /* unusual runtimes */
    }
    return undefined;
}
function defaultInitFromImportMetaEnv(env) {
    const logLevelRaw = env['VITE_LOG_LEVEL'];
    const endpointRaw = env['VITE_LOG_API_ENDPOINT'];
    const logToConsoleRaw = env['VITE_LOG_TO_CONSOLE'];
    return {
        logLevel: normalizeRootLogLevel(typeof logLevelRaw === 'string' ? logLevelRaw : ''),
        logJson: env['VITE_LOG_JSON'] === 'true',
        logToConsole: logToConsoleRaw !== 'false',
        logApiEndpoint: typeof endpointRaw === 'string' && endpointRaw.trim() !== '' ? endpointRaw : undefined,
        app: typeof env['VITE_APP'] === 'string' ? env['VITE_APP'] : '',
        namespace: typeof env['VITE_NAMESPACE'] === 'string' ? env['VITE_NAMESPACE'] : '',
        buildTag: typeof env['VITE_BUILD_TAG'] === 'string' ? env['VITE_BUILD_TAG'] : '',
        auditLogsEnabled: env['VITE_AUDIT_LOGS_ENABLED'] === 'true',
    };
}
/** Default init: Node uses `process.env`; Vite uses `import.meta.env`. */
export function resolveDefaultLoggerInit() {
    if (isLikelyBackendNode()) {
        return buildLoggerInitFromEnv(process.env);
    }
    const viteEnv = tryImportMetaEnv();
    if (viteEnv !== undefined)
        return defaultInitFromImportMetaEnv(viteEnv);
    return {
        logLevel: 'debug',
        logJson: false,
        logToConsole: true,
        logApiEndpoint: undefined,
        app: '',
        namespace: '',
        buildTag: '',
        auditLogsEnabled: false,
    };
}
function mergeLoggerInit(base, overrides) {
    if (!overrides)
        return base;
    const out = { ...base };
    for (const [key, val] of Object.entries(overrides)) {
        if (val !== undefined)
            out[key] = val;
    }
    return out;
}
function remapErrorBindings(obj) {
    if (!(obj.error instanceof Error))
        return obj;
    const next = { ...obj };
    next.err = obj.error;
    delete next.error;
    return next;
}
function bindingsAndMessage(message, meta) {
    const isPlainMeta = meta !== undefined && typeof meta === 'object' && meta !== null && !Array.isArray(meta);
    const mergeObj = isPlainMeta ? remapErrorBindings({ ...meta }) : {};
    return [mergeObj, message];
}
const exposedLevels = ['error', 'warn', 'info', 'debug'];
function emit(pLog, level, message, meta) {
    const [mergeObj, msg] = bindingsAndMessage(message, meta);
    const sink = pLog[level].bind(pLog);
    if (Object.keys(mergeObj).length > 0)
        sink(mergeObj, msg);
    else
        sink(msg);
}
function wrapPino(base) {
    const wrapperMethods = exposedLevels.reduce((acc, level) => {
        acc[level] = function logAtLevel(message, meta) {
            emit(base, level, message, meta);
        };
        return acc;
    }, {});
    return {
        child(options) {
            const raw = options.defaultMeta;
            const bindings = raw !== undefined && raw !== null && typeof raw === 'object' ? raw : {};
            return wrapPino(base.child(bindings));
        },
        ...wrapperMethods,
    };
}
function emitFormattedConsoleLine(formattedLine) {
    if (typeof process !== 'undefined' &&
        typeof process.stdout !== 'undefined' &&
        typeof process.stdout.write === 'function') {
        process.stdout.write(`${formattedLine}\n`);
        return;
    }
    // eslint-disable-next-line no-console -- browsers have no stdout
    console.log(formattedLine);
}
const discardDestination = {
    write() { },
};
export function createLogger(overrides) {
    const merged = mergeLoggerInit(resolveDefaultLoggerInit(), overrides);
    const resolved = resolveLoggerInit(merged);
    const metadata = {
        app: resolved.app,
        namespace: resolved.namespace,
        buildTag: resolved.buildTag,
        instanceId: resolved.instanceId,
    };
    const setLogMetadataInternal = (key, value) => {
        metadata[key] = value;
    };
    const normalizedLevelResolved = normalizeRootLogLevel(resolved.logLevel);
    const consoleDestination = resolved.logToConsole
        ? createLineBufferDestination((singleLine) => {
            const formatted = formatStructuredStdoutLine(singleLine, resolved.logJson, metadata);
            if (formatted)
                emitFormattedConsoleLine(formatted);
        })
        : discardDestination;
    const streams = [
        { level: normalizedLevelResolved, stream: consoleDestination },
    ];
    const apiBase = resolved.logApiEndpoint?.trim();
    if (apiBase) {
        streams.push({
            level: normalizedLevelResolved,
            stream: createLogApiDestination(apiBase, metadata),
        });
    }
    const rootPino = pino({
        level: normalizedLevelResolved,
        serializers: {
            err: pino.stdSerializers.err,
        },
    }, pino.multistream(streams));
    const loggerInstance = wrapPino(rootPino);
    const auditInternal = (auditLog) => {
        if (resolved.auditLogsEnabled) {
            loggerInstance.info('Audit log', auditLog);
        }
    };
    return {
        logger: loggerInstance,
        resolved,
        setLogMetadata: setLogMetadataInternal,
        audit: auditInternal,
    };
}
let defaultLoggerHandle = null;
function getDefaultLoggerHandle() {
    if (!defaultLoggerHandle) {
        defaultLoggerHandle = createLogger();
    }
    return defaultLoggerHandle;
}
/** Root logger — lazily constructed so importing `@cortex/utils` (e.g. `fork` only) does not run Pino in the browser bundle. */
export const logger = new Proxy({}, {
    get(_target, prop, receiver) {
        const root = getDefaultLoggerHandle().logger;
        const value = Reflect.get(root, prop, receiver);
        return typeof value === 'function'
            ? value.bind(root)
            : value;
    },
});
export function createChildLogger(opts) {
    return getDefaultLoggerHandle().logger.child({ defaultMeta: { service: opts.name } });
}
export function setLogMetadata(key, value) {
    getDefaultLoggerHandle().setLogMetadata(key, value);
}
export function audit(auditLog) {
    getDefaultLoggerHandle().audit(auditLog);
}
//# sourceMappingURL=logger.js.map